import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const IMAGE_EXT = /\.(jpe?g|png|gif|webp|svg|bmp)$/i;
const VIDEO_EXT = /\.(mp4|webm|ogg|avi|mov|mkv)$/i;
const RECENT_KEY = 'visor_wiki_recent';
const ARTICLES_PER_TOPIC = 15;

const KnowledgeBase = () => {
  const [view, setView] = useState('list');
  const [hierarchy, setHierarchy] = useState({ topics: [], standalone_articles: [], total: 0 });
  const [topics, setTopics] = useState([]);
  const [expandedTopics, setExpandedTopics] = useState(new Set());
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({ title: '', content: '', summary: '', topic_id: null, tags: [] });
  const [createMode, setCreateMode] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteType, setDeleteType] = useState('article');
  const [showNewTopic, setShowNewTopic] = useState(false);
  const [newTopicTitle, setNewTopicTitle] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState('');
  const [uploadFileSize, setUploadFileSize] = useState(0);
  const [editTab, setEditTab] = useState('edit');
  const [previewFile, setPreviewFile] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [topicShowMore, setTopicShowMore] = useState({});
  const [editorFullscreen, setEditorFullscreen] = useState(false);
  const fileInputRef = useRef(null);

  const getRecentArticles = () => {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      if (!raw) return [];
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr.slice(0, 10) : [];
    } catch { return []; }
  };
  const [recentArticles, setRecentArticles] = useState(getRecentArticles());
  const addToRecent = (article) => {
    if (!article?.id) return;
    setRecentArticles(prev => {
      const next = [{ id: article.id, title: article.title }, ...prev.filter(a => a.id !== article.id)].slice(0, 10);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch (_) {}
      return next;
    });
  };

  const parseToc = (md) => {
    if (!md || typeof md !== 'string') return [];
    const lines = md.split('\n');
    const toc = [];
    lines.forEach(line => {
      const m = line.match(/^(#{2,3})\s+(.+)$/);
      if (m) toc.push({ level: m[1].length, text: m[2].trim(), id: m[2].trim().replace(/\s+/g, '-').replace(/[^\w\u0400-\u04FF-]/gi, '').toLowerCase() });
    });
    return toc;
  };

  const getFileIcon = (filename, mimeType) => {
    if (!filename && !mimeType) return '📎';
    const f = (filename || '').toLowerCase();
    const m = (mimeType || '').toLowerCase();
    if (m.startsWith('image/') || /\.(jpe?g|png|gif|webp|svg|bmp)$/i.test(f)) return '🖼️';
    if (m.startsWith('video/') || /\.(mp4|webm|ogg|avi|mov|mkv)$/i.test(f)) return '🎬';
    if (m.includes('pdf') || f.endsWith('.pdf')) return '📕';
    if (m.includes('sheet') || m.includes('excel') || /\.(xlsx?|csv)$/i.test(f)) return '📊';
    if (m.includes('word') || /\.(docx?|odt)$/i.test(f)) return '📄';
    return '📎';
  };

  const formatSize = (bytes) => {
    if (bytes == null) return '—';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const fetchHierarchy = useCallback(async (s, tag) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoading(true);
    try {
      const params = {};
      if (s && s.trim()) params.search = s.trim();
      if (tag && tag.trim()) params.tag = tag.trim();
      const res = await axios.get('/api/wiki/hierarchy', { params, headers: { Authorization: `Bearer ${token}` } });
      setHierarchy({ topics: res.data.topics || [], standalone_articles: res.data.standalone_articles || [], total: res.data.total || 0 });
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось загрузить данные');
      setHierarchy({ topics: [], standalone_articles: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTopics = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await axios.get('/api/wiki/topics', { headers: { Authorization: `Bearer ${token}` } });
      setTopics(res.data.topics || []);
    } catch {
      setTopics([]);
    }
  };

  const fetchArticle = async (id) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await axios.get(`/api/wiki/articles/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = res.data;
      setSelectedArticle(d);
      setFormData({ title: d.title, content: d.content || '', summary: d.summary || '', topic_id: d.topic_id || null, tags: Array.isArray(d.tags) ? d.tags : [] });
      setEditMode(false);
      fetchFiles(id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Статья не найдена');
    }
  };

  const fetchFiles = async (articleId) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await axios.get(`/api/wiki/articles/${articleId}/files`, { headers: { Authorization: `Bearer ${token}` } });
      setFiles(res.data.files || []);
    } catch {
      setFiles([]);
    }
  };

  useEffect(() => { fetchHierarchy(search, tagFilter); fetchTopics(); }, [fetchHierarchy]);
  useEffect(() => { const t = setTimeout(() => { setSuccess(''); setError(''); }, 5000); return () => clearTimeout(t); }, [success, error]);
  const searchTagChanged = useRef(false);
  useEffect(() => {
    if (!searchTagChanged.current) return;
    const timer = setTimeout(() => { fetchHierarchy(search, tagFilter); }, 350);
    return () => clearTimeout(timer);
  }, [search, tagFilter, fetchHierarchy]);
  useEffect(() => { searchTagChanged.current = true; }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchHierarchy(search, tagFilter);
  };

  const toggleTopic = (id) => {
    setExpandedTopics(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCreateTopic = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token || !newTopicTitle.trim()) return;
    try {
      await axios.post('/api/wiki/topics', { title: newTopicTitle.trim(), description: '' }, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess('Тема создана');
      setShowNewTopic(false);
      setNewTopicTitle('');
      fetchTopics();
      fetchHierarchy(search, tagFilter);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка создания темы');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) return;
    setError('');
    try {
      const payload = { ...formData, topic_id: formData.topic_id || undefined, tags: formData.tags && formData.tags.length ? formData.tags : undefined };
      const res = await axios.post('/api/wiki/articles', payload, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess('Статья создана. Можно загрузить файлы ниже.');
      setSelectedArticle(res.data);
      setFormData({ title: res.data.title, content: res.data.content || '', summary: res.data.summary || '', topic_id: res.data.topic_id || null, tags: Array.isArray(res.data.tags) ? res.data.tags : [] });
      setCreateMode(false);
      setView('article');
      setEditMode(true);
      fetchFiles(res.data.id);
      fetchHierarchy(search, tagFilter);
      fetchTopics();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка создания статьи');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedArticle) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    setError('');
    try {
      const payload = { ...formData, topic_id: formData.topic_id || undefined, tags: formData.tags && formData.tags.length ? formData.tags : undefined };
      const res = await axios.put(`/api/wiki/articles/${selectedArticle.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedArticle(res.data);
      setSuccess('Статья обновлена');
      setEditMode(false);
      fetchHierarchy(search, tagFilter);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка сохранения');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      if (deleteType === 'topic') {
        await axios.delete(`/api/wiki/topics/${deleteConfirm.id}`, { headers: { Authorization: `Bearer ${token}` } });
        setSuccess('Тема и все статьи удалены');
      } else {
        await axios.delete(`/api/wiki/articles/${deleteConfirm.id}`, { headers: { Authorization: `Bearer ${token}` } });
        setSuccess('Статья удалена');
      }
      setDeleteConfirm(null);
      setDeleteType('article');
      setView('list');
      setSelectedArticle(null);
      fetchHierarchy(search, tagFilter);
      fetchTopics();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const handleFileDelete = async (fileId) => {
    const token = localStorage.getItem('token');
    if (!token || !selectedArticle) return;
    try {
      await axios.delete(`/api/wiki/files/${fileId}`, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess('Файл удалён');
      fetchFiles(selectedArticle.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка удаления файла');
    }
  };

  const handleSync = async () => {
    if (!selectedArticle) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    setSyncing(true);
    setError('');
    try {
      const res = await axios.post(`/api/wiki/articles/${selectedArticle.id}/sync`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess(res.data?.message || 'Синхронизация выполнена');
      fetchFiles(selectedArticle.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка синхронизации');
    } finally {
      setSyncing(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target?.files?.[0];
    if (!file || !selectedArticle) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    setUploading(true);
    setUploadProgress(0);
    setUploadFileName(file.name);
    setUploadFileSize(file.size);
    setError('');
    try {
      await axios.post(`/api/wiki/articles/${selectedArticle.id}/upload`, file, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Filename': file.name,
          'Content-Type': 'application/octet-stream'
        },
        timeout: 0,
        onUploadProgress: (ev) => {
          if (ev.total && ev.total > 0) {
            setUploadProgress(Math.round((ev.loaded / ev.total) * 100));
          } else {
            setUploadProgress(ev.loaded ? 50 : 0);
          }
        }
      });
      setUploadProgress(100);
      setSuccess(`Файл «${file.name}» загружен`);
      if (selectedArticle) fetchFiles(selectedArticle.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка загрузки файла');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadFileName('');
      setUploadFileSize(0);
      e.target.value = '';
    }
  };

  const downloadFile = async (fileId) => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`/api/wiki/files/${fileId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const f = files.find(x => x.id === fileId);
      const name = f?.filename || 'download';
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка скачивания');
    }
  };

  const openArticle = (a) => {
    setSelectedArticle(a);
    addToRecent(a);
    fetchArticle(a.id);
    setView('article');
  };

  const backToList = () => {
    setView('list');
    setSelectedArticle(null);
    setEditMode(false);
    setCreateMode(false);
    fetchHierarchy(search, tagFilter);
  };

  const openCreate = (topicId) => {
    setSelectedArticle(null);
    setFormData({ title: '', content: '', summary: '', topic_id: topicId || null, tags: [] });
    setCreateMode(true);
    setView('create');
    setFiles([]);
  };

  const toggleTopicShowMore = (topicId) => {
    setTopicShowMore(prev => ({ ...prev, [topicId]: !prev[topicId] }));
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (previewFile) closePreview();
        else if (showNewTopic) setShowNewTopic(false);
        else if (deleteConfirm) setDeleteConfirm(null);
        else if (view === 'article' || view === 'create') backToList();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 's' && view === 'article' && editMode && selectedArticle) {
        e.preventDefault();
        handleUpdate(e);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [view, editMode, selectedArticle, showNewTopic, deleteConfirm, previewFile]);

  const isPreviewable = (f) => (f.mime_type && (f.mime_type.startsWith('image/') || f.mime_type.startsWith('video/'))) || IMAGE_EXT.test(f.filename) || VIDEO_EXT.test(f.filename);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const openPreview = async (f) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await axios.get(`/api/wiki/files/${f.id}/preview`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      setPreviewBlobUrl(URL.createObjectURL(res.data));
      setPreviewFile(f);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка загрузки');
    }
  };
  const closePreview = () => {
    if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    setPreviewBlobUrl(null);
    setPreviewFile(null);
  };

  const currentTopic = selectedArticle?.topic_id && hierarchy.topics.find(t => t.id === selectedArticle.topic_id);
  const relatedArticles = (view === 'article' && selectedArticle?.topic_id && currentTopic?.articles)
    ? currentTopic.articles.filter(a => a.id !== selectedArticle.id).slice(0, 8)
    : [];

  return (
    <div className="knowledge-base content-area wiki-animated">
      <div className="content-header wiki-header">
        <div>
          {((view === 'article' || view === 'create') && (
            <nav className="wiki-breadcrumbs" aria-label="Навигация">
              <button type="button" className="wiki-breadcrumb-item" onClick={backToList}>База знаний</button>
              {view === 'article' && currentTopic && <span className="wiki-breadcrumb-sep">/</span>}
              {view === 'article' && currentTopic && <span className="wiki-breadcrumb-item wiki-breadcrumb-current">{currentTopic.title}</span>}
              {view === 'article' && selectedArticle && <span className="wiki-breadcrumb-sep">/</span>}
              {view === 'article' && selectedArticle && <span className="wiki-breadcrumb-item wiki-breadcrumb-current">{selectedArticle.title}</span>}
              {view === 'create' && <span className="wiki-breadcrumb-sep">/</span>}
              {view === 'create' && <span className="wiki-breadcrumb-item wiki-breadcrumb-current">Новая статья</span>}
            </nav>
          )) || (
            <>
              <h1 className="wiki-title">База знаний</h1>
              <p className="wiki-subtitle">Темы и статьи. Файлы в каталоге wiki NAS-сервера. Синхронизация с NAS.</p>
            </>
          )}
        </div>
        <div className="header-actions wiki-actions">
          {view === 'list' && (
            <>
              <button type="button" className="btn-secondary btn-small" onClick={() => { setNewTopicTitle(''); setShowNewTopic(true); }}>+ Тема</button>
              <button type="button" className="btn-success btn-small" onClick={() => openCreate(null)}>+ Статья</button>
            </>
          )}
          {(view === 'article' || view === 'create') && (
            <>
              {view === 'create' ? (
                <button type="button" className="btn-success btn-small" onClick={handleCreate}>Создать статью</button>
              ) : editMode ? (
                <button type="button" className="btn-success btn-small" onClick={handleUpdate}>Сохранить</button>
              ) : (
                <button type="button" className="btn-secondary btn-small" onClick={() => setEditMode(true)}>Редактировать</button>
              )}
              {view === 'article' && (
                <button type="button" className="btn-secondary btn-small" onClick={() => { setDeleteConfirm(selectedArticle); setDeleteType('article'); }}>Удалить</button>
              )}
              <button type="button" className="btn-secondary btn-small" onClick={backToList}>← К списку</button>
            </>
          )}
        </div>
      </div>

      {error && <div className="error wiki-message wiki-fade-in">{error}</div>}
      {success && <div className="success wiki-message wiki-fade-in">{success}</div>}

      {uploading && (
        <div className="wiki-upload-progress wiki-fade-in">
          <div className="wiki-upload-header">
            <span className="wiki-upload-name">{uploadFileName}</span>
            <span className="wiki-upload-size">{formatSize(uploadFileSize)}</span>
          </div>
          <div className="wiki-progress-bar-wrap">
            <div className="wiki-progress-bar" style={{ width: uploadProgress + '%' }} />
          </div>
          <div className="wiki-upload-percent">{uploadProgress}%</div>
        </div>
      )}

      <div className={`wiki-layout ${sidebarCollapsed ? 'wiki-sidebar-collapsed' : ''}`}>
        <aside className="wiki-sidebar">
          <button type="button" className="wiki-sidebar-toggle" onClick={() => setSidebarCollapsed(!sidebarCollapsed)} title={sidebarCollapsed ? 'Показать дерево' : 'Свернуть дерево'} aria-label={sidebarCollapsed ? 'Показать дерево' : 'Свернуть дерево'}>
            {sidebarCollapsed ? '▶' : '◀'}
          </button>
          {!sidebarCollapsed && (
            <div className="wiki-sidebar-tree">
              {hierarchy.topics.map((t) => {
                const arts = t.articles || [];
                const showAll = topicShowMore[t.id];
                const visible = showAll ? arts : arts.slice(0, ARTICLES_PER_TOPIC);
                const hasMore = arts.length > ARTICLES_PER_TOPIC;
                return (
                  <div key={t.id} className="wiki-topic-block wiki-sidebar-topic">
                    <div className="wiki-topic-row">
                      <button type="button" className="wiki-topic-toggle" onClick={() => toggleTopic(t.id)}>{expandedTopics.has(t.id) ? '▼' : '▶'}</button>
                      <span className="wiki-topic-icon">📁</span>
                      <button type="button" className="wiki-topic-title" onClick={() => toggleTopic(t.id)}>{t.title}</button>
                      <span className="wiki-topic-count">({arts.length})</span>
                    </div>
                    {expandedTopics.has(t.id) && (
                      <ul className="wiki-topic-articles">
                        {visible.map((a) => (
                          <li key={a.id} className={`wiki-article-row ${selectedArticle?.id === a.id ? 'wiki-article-current' : ''}`}>
                            <span className="wiki-article-icon">📄</span>
                            <button type="button" className="wiki-article-link" onClick={() => openArticle(a)}>{a.title}</button>
                          </li>
                        ))}
                        {hasMore && !showAll && <li><button type="button" className="wiki-show-more" onClick={() => toggleTopicShowMore(t.id)}>Показать ещё ({arts.length - ARTICLES_PER_TOPIC})</button></li>}
                        {hasMore && showAll && <li><button type="button" className="wiki-show-more" onClick={() => toggleTopicShowMore(t.id)}>Свернуть</button></li>}
                      </ul>
                    )}
                    {expandedTopics.has(t.id) && t.description && <div className="wiki-topic-description">{t.description}</div>}
                  </div>
                );
              })}
              {hierarchy.standalone_articles && hierarchy.standalone_articles.length > 0 && (
                <div className="wiki-standalone-block">
                  <div className="wiki-standalone-title">Отдельные статьи</div>
                  <ul className="wiki-topic-articles">
                    {hierarchy.standalone_articles.map((a) => (
                      <li key={a.id} className={`wiki-article-row ${selectedArticle?.id === a.id ? 'wiki-article-current' : ''}`}>
                        <span className="wiki-article-icon">📄</span>
                        <button type="button" className="wiki-article-link" onClick={() => openArticle(a)}>{a.title}</button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </aside>
        <main className="wiki-main">
      {view === 'list' && (
        <div className="wiki-list-view wiki-fade-in">
          <form className="wiki-search-form" onSubmit={handleSearch}>
            <input
              type="text"
              className="wiki-search-input"
              placeholder="Поиск по заголовку и содержимому..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <input
              type="text"
              className="wiki-search-input wiki-tag-filter"
              placeholder="Тег (фильтр)"
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
            />
            <button type="submit" className="btn-secondary btn-small">Найти</button>
          </form>

          {recentArticles.length > 0 && (
            <div className="wiki-recent-block">
              <div className="wiki-recent-title">Недавно просмотренные</div>
              <div className="wiki-recent-links">
                {recentArticles.map((r) => (
                  <button key={r.id} type="button" className="wiki-recent-link" onClick={() => openArticle(r)}>{r.title}</button>
                ))}
              </div>
            </div>
          )}

          {loading ? (
            <div className="wiki-loading">
              <div className="wiki-spinner" />
              <span>Загрузка...</span>
            </div>
          ) : (
            <div className="wiki-hierarchy-list">
              {hierarchy.topics.map((t) => {
                const arts = t.articles || [];
                const showAll = topicShowMore[t.id];
                const visible = showAll ? arts : arts.slice(0, ARTICLES_PER_TOPIC);
                const hasMore = arts.length > ARTICLES_PER_TOPIC;
                return (
                <div key={t.id} className="wiki-topic-block">
                  <div className="wiki-topic-row">
                    <button type="button" className="wiki-topic-toggle" onClick={() => toggleTopic(t.id)}>{expandedTopics.has(t.id) ? '▼' : '▶'}</button>
                    <span className="wiki-topic-icon">📁</span>
                    <button type="button" className="wiki-topic-title" onClick={() => toggleTopic(t.id)}>{t.title}</button>
                    <span className="wiki-topic-count">({arts.length})</span>
                    <button type="button" className="btn-icon-small" onClick={() => openCreate(t.id)} title="Добавить статью">➕</button>
                    <button type="button" className="btn-icon-small" onClick={() => { setDeleteConfirm({ id: t.id, title: t.title }); setDeleteType('topic'); }} title="Удалить тему">🗑️</button>
                  </div>
                  {expandedTopics.has(t.id) && t.description && <div className="wiki-topic-description wiki-topic-description-inline">{t.description}</div>}
                  {expandedTopics.has(t.id) && arts.length > 0 && (
                    <ul className="wiki-topic-articles">
                      {visible.map((a) => (
                        <li key={a.id} className="wiki-article-row">
                          <span className="wiki-article-icon">📄</span>
                          <button type="button" className="wiki-article-link" onClick={() => openArticle(a)}>{a.title}</button>
                          <span className="wiki-article-meta">{formatDate(a.updated_at || a.created_at)}</span>
                          {a.tags && a.tags.length > 0 && <span className="wiki-article-tags">{a.tags.slice(0, 3).join(', ')}</span>}
                          <button type="button" className="btn-icon-small" onClick={(ev) => { ev.stopPropagation(); setDeleteConfirm(a); setDeleteType('article'); }} title="Удалить">🗑️</button>
                        </li>
                      ))}
                      {hasMore && !showAll && <li><button type="button" className="wiki-show-more" onClick={() => toggleTopicShowMore(t.id)}>Показать ещё ({arts.length - ARTICLES_PER_TOPIC})</button></li>}
                      {hasMore && showAll && <li><button type="button" className="wiki-show-more" onClick={() => toggleTopicShowMore(t.id)}>Свернуть</button></li>}
                    </ul>
                  )}
                </div>
                );
              })}
              {hierarchy.standalone_articles && hierarchy.standalone_articles.length > 0 && (
                <div className="wiki-standalone-block">
                  <div className="wiki-standalone-title">Отдельные статьи</div>
                  <ul className="wiki-topic-articles">
                    {hierarchy.standalone_articles.map((a) => (
                      <li key={a.id} className="wiki-article-row">
                        <span className="wiki-article-icon">📄</span>
                        <button type="button" className="wiki-article-link" onClick={() => openArticle(a)}>{a.title}</button>
                        <span className="wiki-article-meta">{formatDate(a.updated_at || a.created_at)}</span>
                        <button type="button" className="btn-icon-small" onClick={() => { setDeleteConfirm(a); setDeleteType('article'); }} title="Удалить">🗑️</button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {hierarchy.total === 0 && !loading && (
                <div className="wiki-empty-state">
                  <p className="wiki-empty-title">Тем и статей пока нет</p>
                  <p className="wiki-empty-desc">Здесь можно хранить инструкции, регламенты и документацию. Создайте тему для раздела или статью.</p>
                  <div className="wiki-empty-actions">
                    <button type="button" className="btn-secondary" onClick={() => { setNewTopicTitle(''); setShowNewTopic(true); }}>Создать тему</button>
                    <button type="button" className="btn-success" onClick={() => openCreate(null)}>Создать статью</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {view === 'create' && (
        <div className="wiki-create-view wiki-fade-in">
          <form onSubmit={handleCreate} className="wiki-editor-form wiki-full-form">
            <div className="form-group">
              <label>Тема (опционально)</label>
              <select value={formData.topic_id || ''} onChange={(e) => setFormData({ ...formData, topic_id: e.target.value ? parseInt(e.target.value, 10) : null })} title="Раздел Wiki, к которому относится статья">
                <option value="">— Без темы —</option>
                {topics.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Заголовок</label>
              <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required placeholder="Название статьи" title="Заголовок статьи, отображается в списке и в шапке" />
            </div>
            <div className="form-group">
              <label>Краткое описание</label>
              <input type="text" value={formData.summary} onChange={(e) => setFormData({ ...formData, summary: e.target.value })} placeholder="Необязательно" title="Краткое описание для превью и поиска" />
            </div>
            <div className="form-group">
              <label>Теги (через запятую)</label>
              <input type="text" value={(formData.tags || []).join(', ')} onChange={(e) => setFormData({ ...formData, tags: (e.target.value || '').split(',').map(x => x.trim()).filter(Boolean) })} placeholder="например: инструкция, 2024" title="Теги для фильтрации и поиска" />
            </div>
            <div className="form-group">
              <label>Содержимое (Markdown)</label>
              <textarea rows={16} value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} placeholder="Поддерживается Markdown" title="Текст статьи. Поддерживается разметка Markdown: заголовки, списки, ссылки, код" />
            </div>
          </form>
          <p className="wiki-hint">После создания статьи вы сможете загрузить файлы в разделе «Файлы статьи». Сохранение: Ctrl+S.</p>
        </div>
      )}

      {view === 'article' && selectedArticle && (
        <div className={`wiki-article-view wiki-fade-in ${editorFullscreen ? 'wiki-editor-fullscreen' : ''}`}>
          {editMode ? (
            <div className="wiki-editor">
              <div className="wiki-editor-tabs">
                <button type="button" className={editTab === 'edit' ? 'active' : ''} onClick={() => setEditTab('edit')}>Редактор</button>
                <button type="button" className={editTab === 'preview' ? 'active' : ''} onClick={() => setEditTab('preview')}>Предпросмотр</button>
                <button type="button" className="wiki-fullscreen-toggle" onClick={() => setEditorFullscreen(!editorFullscreen)} title={editorFullscreen ? 'Выйти из полноэкранного режима' : 'Редактор на весь экран'}>{editorFullscreen ? '✕ Полный экран' : '⛶ Полный экран'}</button>
              </div>
              {editTab === 'edit' && (
                <form onSubmit={handleUpdate} className="wiki-editor-form">
                  <div className="form-group">
                    <label>Тема</label>
                    <select value={formData.topic_id || ''} onChange={(e) => setFormData({ ...formData, topic_id: e.target.value ? parseInt(e.target.value, 10) : null })} title="Раздел Wiki">
                      <option value="">— Без темы —</option>
                      {topics.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Заголовок</label>
                    <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required title="Заголовок статьи" />
                  </div>
                  <div className="form-group">
                    <label>Краткое описание</label>
                    <input type="text" value={formData.summary} onChange={(e) => setFormData({ ...formData, summary: e.target.value })} placeholder="Краткое описание статьи" title="Краткое описание для превью" />
                  </div>
                  <div className="form-group">
                    <label>Теги (через запятую)</label>
                    <input type="text" value={(formData.tags || []).join(', ')} onChange={(e) => setFormData({ ...formData, tags: (e.target.value || '').split(',').map(x => x.trim()).filter(Boolean) })} placeholder="например: инструкция, 2024" />
                  </div>
                  <div className="form-group">
                    <label>Содержимое (Markdown)</label>
                    <textarea rows={20} value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} placeholder="Поддерживается Markdown" title="Текст в формате Markdown" />
                  </div>
                </form>
              )}
              {editTab === 'preview' && (
                <div className="wiki-preview">
                  <h2>{formData.title}</h2>
                  <ReactMarkdown>{formData.content || `*Пусто*`}</ReactMarkdown>
                </div>
              )}
            </div>
          ) : (
            <div className="wiki-article-content">
              <div className="wiki-article-body-with-toc">
                <div className="wiki-article-main">
                  <h1 className="wiki-article-title">{selectedArticle.title}</h1>
                  {selectedArticle.summary && <p className="wiki-article-summary">{selectedArticle.summary}</p>}
                  {(selectedArticle.tags && selectedArticle.tags.length > 0) && (
                    <p className="wiki-article-tags-inline">Теги: {(selectedArticle.tags || []).join(', ')}</p>
                  )}
                  <div className="wiki-markdown-body">
                    <ReactMarkdown components={{ h2: ({ node, children, ...p }) => <h2 id={(String(children).replace(/\s+/g, '-').replace(/[^\w\u0400-\u04FF-]/gi, '').toLowerCase())} {...p}>{children}</h2>, h3: ({ node, children, ...p }) => <h3 id={(String(children).replace(/\s+/g, '-').replace(/[^\w\u0400-\u04FF-]/gi, '').toLowerCase())} {...p}>{children}</h3> }}>{selectedArticle.content || `*Содержимое пусто*`}</ReactMarkdown>
                  </div>
                </div>
                {parseToc(selectedArticle.content || '').length > 0 && (
                  <nav className="wiki-toc" aria-label="Содержание">
                    <div className="wiki-toc-title">Содержание</div>
                    <ul className="wiki-toc-list">
                      {parseToc(selectedArticle.content).map((item, i) => (
                        <li key={i} className={`wiki-toc-item wiki-toc-level-${item.level}`}><a href={`#${item.id}`} className="wiki-toc-link">{item.text}</a></li>
                      ))}
                    </ul>
                  </nav>
                )}
              </div>
              {relatedArticles.length > 0 && (
                <div className="wiki-related-section">
                  <h3 className="wiki-related-title">Статьи в этой теме</h3>
                  <ul className="wiki-related-list">
                    {relatedArticles.map((a) => (
                      <li key={a.id}><button type="button" className="wiki-related-link" onClick={() => openArticle(a)}>{a.title}</button></li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="wiki-files-divider" role="separator" />
          <div className="wiki-files-section">
            <h3>Файлы статьи</h3>
            <div className="wiki-files-actions">
              <button type="button" className="btn-success btn-small" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                {uploading ? 'Загрузка…' : 'Загрузить файл'}
              </button>
              <button type="button" className="btn-secondary btn-small" onClick={handleSync} disabled={syncing}>
                {syncing ? 'Синхронизация…' : 'Синхронизировать с NAS'}
              </button>
              <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileUpload} />
            </div>
            <ul className="wiki-files-list">
              {files.map((f) => (
                <li key={f.id} className="wiki-file-item">
                  <span className="wiki-file-icon" title={f.mime_type || ''}>{getFileIcon(f.filename, f.mime_type)}</span>
                  <span className="wiki-file-name">{f.filename}</span>
                  {!f.exists_on_nas && <span className="wiki-file-missing" title="Файл удалён в NAS">⚠️</span>}
                  <span className="wiki-file-size">{formatSize(f.file_size)}</span>
                  {isPreviewable(f) && (
                    <button type="button" className="btn-icon-small" onClick={() => openPreview(f)} title="Просмотр">👁️</button>
                  )}
                  <button type="button" className="btn-icon-small" onClick={() => downloadFile(f.id)} title="Скачать">⬇️</button>
                  {editMode && (
                    <button type="button" className="btn-icon-small" onClick={() => handleFileDelete(f.id)} title="Удалить">🗑️</button>
                  )}
                </li>
              ))}
              {files.length === 0 && !uploading && (
                <li className="wiki-files-empty">Нет загруженных файлов. Файлы в каталоге wiki NAS-сервера.</li>
              )}
            </ul>
          </div>
        </div>
      )}
        </main>
      </div>

      {previewFile && previewBlobUrl && (
        <div className="modal-overlay wiki-preview-overlay" onClick={closePreview}>
          <div className="wiki-preview-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="wiki-preview-close" onClick={closePreview}>✕</button>
            {IMAGE_EXT.test(previewFile.filename) || (previewFile.mime_type && previewFile.mime_type.startsWith('image/')) ? (
              <img src={previewBlobUrl} alt={previewFile.filename} className="wiki-preview-media" />
            ) : (
              <video src={previewBlobUrl} controls className="wiki-preview-media" />
            )}
          </div>
        </div>
      )}

      {showNewTopic && (
        <div className="modal-overlay" onClick={() => setShowNewTopic(false)}>
          <div className="modal wiki-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Новая тема</h2>
            <form onSubmit={handleCreateTopic}>
              <div className="form-group">
                <label>Название темы</label>
                <input type="text" value={newTopicTitle} onChange={(e) => setNewTopicTitle(e.target.value)} required placeholder="Название" autoFocus title="Название темы раздела Wiki (например: Документация, Регламенты)" />
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn-success">Создать</button>
                <button type="button" className="btn-secondary" onClick={() => setShowNewTopic(false)}>Отмена</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="confirmation-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>{deleteType === 'topic' ? 'Удалить тему?' : 'Удалить статью?'}</h3>
            <p>{deleteType === 'topic'
              ? `Удалить тему «${deleteConfirm.title}»? Все статьи и файлы внутри будут удалены.`
              : `Удалить статью «${deleteConfirm.title}»? Все прикреплённые файлы будут удалены.`}</p>
            <div className="confirmation-actions">
              <button type="button" className="btn-danger btn-small" onClick={handleDelete}>Удалить</button>
              <button type="button" className="btn-secondary btn-small" onClick={() => { setDeleteConfirm(null); setDeleteType('article'); }}>Отмена</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBase;
